import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetEventCTAButtonsDataQuery } from '../../components/CtaButtons/CtaButtons.graphql.generated';
import { useGetRsvpDeadlineQuery } from '../../components/CtaButtons/RsvpDeadline.graphql.generated';

/**
 * Single source of truth for RSVP deadline gating across guest surfaces.
 *
 * The deadline query is keyed on the event id. We resolve it primarily from
 * `useEventInfo()` (`eventInfo.eventId`), which is seeded from the SSR-embedded
 * `__INITIAL_EVENT_ID__` and stays stable across renders. The handle-keyed
 * `GetEventCTAButtonsData` query (`eventByName.id`) is kept only as a fallback:
 * because it runs `ssr: false`, its result flickers between the id and
 * `undefined` across the many guest surfaces that mount this guard, and keying
 * on it alone made the deadline query re-skip mid-render — the deadline (and
 * the post-deadline button suppression) would drop out whenever it briefly
 * resolved to `undefined`. See LEGRSVP-44.
 */
export const useRsvpDeadlineGuard = () => {
  const [isRsvpDeadlineEnabled] = useIsFeatureEnabled('rsvpDeadlineEnabled');
  const { eventName } = useEventNameMatch();
  const { eventInfo } = useEventInfo();

  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    skip: !eventName,
    ssr: false,
  });
  const eventId = eventInfo?.eventId ?? sessionData?.eventByName?.id;

  const { data: deadlineData } = useGetRsvpDeadlineQuery({
    variables: { eventId: eventId || '' },
    batchMode: 'fast',
    skip: !eventId || !isRsvpDeadlineEnabled,
    ssr: false,
  });

  const rsvpDeadline = deadlineData?.getRsvpSettingsByEventId;
  const isRsvpClosedByDeadline = isRsvpDeadlineEnabled && rsvpDeadline?.isOpen === false;
  const showDeadlineToGuest = isRsvpDeadlineEnabled && rsvpDeadline?.showDeadline === true && rsvpDeadline?.deadlineDate != null;
  const deadlineDate = rsvpDeadline?.deadlineDate ?? null;
  // Custom post-deadline note, surfaced only once RSVP is actually closed.
  // Null/empty means no override — guest surfaces render nothing in that case.
  const deadlineNote = isRsvpClosedByDeadline ? rsvpDeadline?.deadlineNote?.trim() || null : null;
  // Resolved server-side, ungated: the RSVP deadline is not covered by the event
  // date/location password gates, so the BE returns the event timezone even when
  // `EventInfo.timezone` is null for locked guests. Fall back to UTC only when
  // the BE couldn't resolve it (matches the pre-fix behavior for that case).
  const deadlineTimezone = rsvpDeadline?.deadlineTimezone ?? 'UTC';

  return { isRsvpClosedByDeadline, showDeadlineToGuest, deadlineDate, deadlineNote, deadlineTimezone };
};
